import React from 'react';
import { ExternalLink, ShoppingBag, Sparkles, Heart, ArrowRight } from 'lucide-react';

/**
 * BmoreWealthyAttribution
 * -----------------------
 * Bottom-of-page creator card: tells visitors who built the dashboard and
 * how to support continued development (the brand sells merch). Distinct
 * from the legal disclaimer footer below it — that one stays clinical,
 * this one carries the brand voice.
 *
 * Design intent:
 *   - Reads as appreciation, not as an ad. The CTAs are clear but secondary
 *     to the "thanks for visiting / made with care" tone.
 *   - Visual interest comes from ambient motion (slow halo pulses, gradient
 *     brand-name text, hover microinteractions on shield + CTAs) — never
 *     from disruptive animation.
 *   - Honors `prefers-reduced-motion` — all pulse / shimmer animations
 *     suppress when the user has motion-reduction enabled.
 *
 * To use the actual BMore Wealthy logo asset:
 *   1. Save the file to `public/bmore-wealthy-logo.png` (or .svg)
 *   2. Replace `<BmoreShieldStylized />` with `<img src="/bmore-wealthy-logo.png" .../>`
 */
const BRAND_URL = 'https://www.bmorewealthy.net';

export default function BmoreWealthyAttribution() {
  return (
    <section className="relative overflow-hidden rounded-2xl border border-amber-500/25 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-7 md:p-10">
      {/* Ambient corner glows — slow pulse so the card feels "alive" without
          competing with the data sections above it for attention */}
      <div className="absolute -top-24 -right-24 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl pointer-events-none bw-pulse" />
      <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-orange-500/10 rounded-full blur-3xl pointer-events-none bw-pulse-delayed" />

      {/* Hairline accent across the top */}
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" />

      <div className="relative grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 md:gap-10 items-center">
        <BmoreShieldStylized />

        <div className="text-center md:text-left">
          {/* Pre-headline tag */}
          <div className="inline-flex items-center gap-2 text-[10px] font-bold tracking-widest uppercase text-amber-300 mb-3 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30">
            <Sparkles className="w-3 h-3" />
            Free dashboard · made with care
          </div>

          {/* Main heading — brand name in gradient text to anchor identity */}
          <h2 className="text-2xl md:text-3xl font-bold text-slate-100 tracking-tight">
            Built by{' '}
            <span className="bg-gradient-to-r from-amber-300 via-amber-400 to-orange-400 bg-clip-text text-transparent">
              BMore Wealthy
            </span>
          </h2>

          {/* Body — explain the offer and the support ask in one breath */}
          <p className="text-sm text-slate-400 mt-3 leading-relaxed max-w-prose mx-auto md:mx-0">
            This dashboard is open and free for anyone learning how macro forces — rates, inflation,
            on-chain activity — actually shape Bitcoin's price. If it has helped you, the best way to
            back continued development is to support the brand directly.
          </p>

          {/* Tagline mark — flanking lines give it weight without being heavy */}
          <div className="mt-5 inline-flex items-center gap-2.5 text-[11px] tracking-[0.35em] font-bold uppercase font-mono text-amber-300/80">
            <span className="w-6 h-px bg-amber-400/50" />
            <span>
              Inve<span className="text-amber-300">$</span>t in your future self
            </span>
            <span className="w-6 h-px bg-amber-400/50" />
          </div>

          {/* CTAs */}
          <div className="flex flex-wrap gap-3 justify-center md:justify-start mt-6">
            <a
              href={BRAND_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="bw-cta-primary group inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-slate-950 font-bold text-sm shadow-lg shadow-amber-500/25 hover:shadow-amber-500/45 transition-all"
            >
              <ShoppingBag className="w-4 h-4" />
              <span>Shop the brand</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </a>
            <a
              href={BRAND_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-amber-500/40 text-amber-300 font-semibold text-sm hover:bg-amber-500/10 hover:border-amber-400/60 transition-colors"
            >
              <span>bmorewealthy.net</span>
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>

          {/* Sub-line — humanises the section without being saccharine */}
          <div className="mt-5 flex items-center gap-1.5 justify-center md:justify-start text-[11px] text-slate-500">
            <Heart className="w-3 h-3 text-amber-400/70 fill-amber-400/30" />
            <span>Built for the community of curious investors</span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes bw-halo-pulse {
          0%, 100% { opacity: 0.45; transform: scale(1); }
          50%      { opacity: 0.7;  transform: scale(1.08); }
        }
        .bw-pulse { animation: bw-halo-pulse 6s ease-in-out infinite; }
        .bw-pulse-delayed { animation: bw-halo-pulse 6s ease-in-out 3s infinite; }

        /* Primary CTA gets a subtle scale on hover (more pronounced than the
           outline button to clarify the visual hierarchy of the two CTAs) */
        .bw-cta-primary { transform: translateY(0) scale(1); transition: transform .15s, box-shadow .25s; }
        .bw-cta-primary:hover  { transform: translateY(-1px) scale(1.02); }
        .bw-cta-primary:active { transform: translateY(0)    scale(0.98); }

        /* Accessibility — quiet all motion if the user has prefers-reduced-motion */
        @media (prefers-reduced-motion: reduce) {
          .bw-pulse, .bw-pulse-delayed { animation: none; }
          .bw-cta-primary, .bw-cta-primary:hover, .bw-cta-primary:active { transform: none; }
        }
      `}</style>
    </section>
  );
}

// ─── Subcomponents ──────────────────────────────────────────────────────────

/**
 * Stylized SVG shield — placeholder until the actual brand asset is dropped
 * in `public/`. Captures the spirit of the BMore Wealthy mark (shield silhouette,
 * "BMORE WEALTHY" wordmark, upward-trending growth motif, ₿ Bitcoin glyph) without
 * trying to fake the bull illustration — which would look amateurish at this size.
 */
function BmoreShieldStylized() {
  return (
    <a
      href={BRAND_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="bw-shield group block relative w-36 h-44 mx-auto md:w-40 md:h-48"
      aria-label="Visit BMore Wealthy"
    >
      {/* Soft halo behind the shield — intensifies on hover */}
      <div className="absolute inset-0 bg-amber-500/30 rounded-2xl blur-2xl opacity-50 group-hover:opacity-90 transition-opacity duration-500" />

      <svg
        viewBox="0 0 120 144"
        className="relative w-full h-full transition-transform duration-500 group-hover:scale-105"
      >
        <defs>
          <linearGradient id="bwShieldFill" x1="0" y1="0" x2="0.5" y2="1">
            <stop offset="0"   stopColor="#fbbf24" />
            <stop offset="0.5" stopColor="#d97706" />
            <stop offset="1"   stopColor="#7c2d12" />
          </linearGradient>
          <linearGradient id="bwShieldEdge" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#fde68a" />
            <stop offset="1" stopColor="#b45309" />
          </linearGradient>
        </defs>

        {/* Shield silhouette */}
        <path
          d="M 60 4 L 110 18 L 110 70 Q 110 122 60 140 Q 10 122 10 70 L 10 18 Z"
          fill="url(#bwShieldFill)"
          stroke="url(#bwShieldEdge)"
          strokeWidth="2.5"
        />

        {/* Wordmark */}
        <text
          x="60" y="28"
          textAnchor="middle"
          fontSize="9.5"
          fontWeight="800"
          letterSpacing="0.6"
          fill="#1c1917"
          fontFamily="ui-sans-serif, system-ui, sans-serif"
        >
          BMORE WEALTHY
        </text>

        {/* Ascending growth bars (left side) */}
        <g fill="white">
          <rect x="20" y="100" width="9" height="20" rx="1.2" opacity="0.85" />
          <rect x="33" y="88"  width="9" height="32" rx="1.2" opacity="0.92" />
          <rect x="46" y="74"  width="9" height="46" rx="1.2" />
        </g>

        {/* Up-arrow rising from the bars to the upper-right */}
        <path
          d="M 22 102 L 38 90 L 51 78 L 80 50"
          stroke="white"
          strokeWidth="2.5"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M 72 50 L 80 50 L 80 58"
          stroke="white"
          strokeWidth="2.5"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* ₿ Bitcoin glyph — thematic anchor for this dashboard */}
        <text
          x="89" y="68"
          textAnchor="middle"
          fontSize="22"
          fontWeight="900"
          fill="white"
          fontFamily="ui-sans-serif, system-ui, sans-serif"
        >
          ₿
        </text>
      </svg>
    </a>
  );
}
